import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr, downloadXlsx } from "@/lib/api";
import { fmtEuro, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/ui/sonner";
import { Link } from "react-router-dom";
import { Plus, Trash2, Landmark, Upload, Download } from "lucide-react";

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function Bookings({ view = "loans" }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const canEdit = ["owner", "hr"].includes(user?.role);
  const isCheques = view === "cheques";
  const [ob, setOb] = useState({
    type: isCheques ? "cheque" : "loan", creditor: "", description: "", amount_total: 0, instalment: 0,
    start_date: todayStr(), months: 12, frequency: "monthly", day_of_month: "", cost_center: "SHARED",
  });

  const { data: meta } = useQuery({ queryKey: ["finance-accounts"], queryFn: async () => (await api.get("/finance/accounts")).data });
  const { data: obligations = [] } = useQuery({ queryKey: ["obligations"], queryFn: async () => (await api.get("/finance/obligations")).data });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["obligations"] });
    qc.invalidateQueries({ queryKey: ["cashflow"] });
  };
  const addOb = useMutation({
    mutationFn: async () => (await api.post("/finance/obligations", {
      ...ob, amount_total: Number(ob.amount_total) || 0, instalment: Number(ob.instalment) || 0,
      months: Number(ob.months) || 1, day_of_month: ob.day_of_month ? Number(ob.day_of_month) : null,
    })).data,
    onSuccess: () => { refresh(); toast.success("Η υποχρέωση καταχωρήθηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const delOb = useMutation({
    mutationFn: async (id) => (await api.delete(`/finance/obligations/${id}`)).data,
    onSuccess: () => { refresh(); toast.success("Διαγράφηκε"); },
  });

  const inp = "mt-1 w-full rounded-lg border border-[#E3DED4] px-3 py-2 text-sm outline-none focus:border-[#45423C]";
  const lab = "text-xs uppercase tracking-wider text-[#6F6A60] font-semibold";
  const obMonthly = obligations.reduce((s, o) => s + (o.active ? o.instalment : 0), 0);
  const loans = obligations.filter((o) => o.type !== "cheque");
  const cheques = obligations.filter((o) => o.type === "cheque");
  const chequesTotal = cheques.reduce((s, o) => s + o.instalment, 0);
  const list = isCheques ? cheques : loans;
  const loansMonthly = loans.reduce((s, o) => s + (o.active ? o.instalment : 0), 0);

  return (
    <div className="space-y-6" data-testid="bookings-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Υποχρεώσεις Ταμείου</p>
          <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">{isCheques ? "Επιταγές" : "Δάνεια"}</h1>
          <p className="text-[#6F6A60] text-sm mt-1">{isCheques
            ? "Επιταγές πληρωτέες και οι ημερομηνίες τους."
            : "Δάνεια και ρυθμίσεις με τις δόσεις τους. Οι επιταγές βρίσκονται στη σελίδα «Επιταγές»."}</p>
        </div>
        {canEdit && (
          <div className="flex items-end gap-2 flex-wrap">
            <button data-testid="ob-template" onClick={() => downloadXlsx("/finance/cashflow-template.xlsx?only=obligations", "TAF_Template_Ypochreoseis.xlsx")}
              className="inline-flex items-center gap-2 rounded-xl border border-[#E3DED4] bg-white px-4 py-2.5 text-sm font-semibold text-[#6E7A63] hover:border-[#6E7A63]">
              <Download className="h-4 w-4" /> Πρότυπο Excel
            </button>
            <Link to="/imports" data-testid="bk-goto-imports"
              className="inline-flex items-center gap-2 rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#56524A]">
              <Upload className="h-4 w-4" /> Εισαγωγές Excel
            </Link>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {(isCheques
          ? [["Επιταγές", cheques.length], ["Αξία επιταγών", fmtEuro(chequesTotal)]]
          : [["Δάνεια & ρυθμίσεις", loans.length], ["Δόσεις / μήνα", fmtEuro(loansMonthly)]]).map(([l, v]) => (
          <div key={l} className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid={`bk-kpi-${l}`}>
            <div className="text-[10px] uppercase tracking-widest text-[#6F6A60]">{l}</div>
            <div className="font-serif text-2xl mt-2 text-[#45423C] tabular">{v}</div>
          </div>
        ))}
      </div>

      {canEdit && (
        <div className="rounded-2xl bg-white border border-[#E3DED4] p-5" data-testid="ob-form">
          <div className="flex items-center gap-2 mb-4">
            <Landmark className="h-4 w-4 text-[#C86235]" />
            <h2 className="font-medium text-[#45423C]">Νέα υποχρέωση / δόση</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div className="col-span-2">
              <label className={lab}>Τύπος</label>
              <select data-testid="ob-type" className={inp} value={ob.type} onChange={(e) => setOb({ ...ob, type: e.target.value })}>
                {Object.entries(meta?.obligation_types || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className={lab}>Πιστωτής</label>
              <input data-testid="ob-creditor" className={inp} value={ob.creditor} onChange={(e) => setOb({ ...ob, creditor: e.target.value })} />
            </div>
            <div className="col-span-2">
              <label className={lab}>Περιγραφή</label>
              <input data-testid="ob-desc" className={inp} value={ob.description} onChange={(e) => setOb({ ...ob, description: e.target.value })} />
            </div>
            <div>
              <label className={lab}>Συνολικό (€)</label>
              <input data-testid="ob-total" type="number" className={inp} value={ob.amount_total} onChange={(e) => setOb({ ...ob, amount_total: e.target.value })} />
            </div>
            <div>
              <label className={lab}>Δόση (€)</label>
              <input data-testid="ob-instalment" type="number" className={inp} value={ob.instalment} onChange={(e) => setOb({ ...ob, instalment: e.target.value })} />
            </div>
            <div>
              <label className={lab}>Έναρξη</label>
              <input data-testid="ob-start" type="date" className={inp} value={ob.start_date} onChange={(e) => setOb({ ...ob, start_date: e.target.value })} />
            </div>
            <div>
              <label className={lab}>Δόσεις</label>
              <input data-testid="ob-months" type="number" className={inp} value={ob.months} onChange={(e) => setOb({ ...ob, months: e.target.value })} />
            </div>
            <div>
              <label className={lab}>Συχνότητα</label>
              <select data-testid="ob-frequency" className={inp} value={ob.frequency} onChange={(e) => setOb({ ...ob, frequency: e.target.value })}>
                <option value="monthly">Μηνιαία</option>
                <option value="quarterly">Ανά 3 μήνες</option>
              </select>
            </div>
            <div>
              <label className={lab}>Ημέρα μήνα</label>
              <input data-testid="ob-day" type="number" className={inp} value={ob.day_of_month} onChange={(e) => setOb({ ...ob, day_of_month: e.target.value })} placeholder="auto" />
            </div>
            <div className="flex items-end">
              <button data-testid="ob-save" onClick={() => addOb.mutate()} disabled={addOb.isPending}
                className="inline-flex items-center justify-center gap-2 w-full rounded-xl bg-[#45423C] text-white px-4 py-2.5 text-sm font-semibold hover:bg-[#56524A] disabled:opacity-50">
                <Plus className="h-4 w-4" /> Καταχώρηση
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4]"><h2 className="font-medium text-[#45423C]">{isCheques ? "Επιταγές πληρωτέες" : "Δάνεια & ρυθμίσεις"}</h2></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="bg-[#F1EDE6] text-left text-[11px] uppercase tracking-wider text-[#6F6A60]">
                <th className="px-4 py-3">Τύπος</th><th className="px-4 py-3">Πιστωτής</th><th className="px-4 py-3">Περιγραφή</th>
                <th className="px-4 py-3 text-right">Συνολικό</th><th className="px-4 py-3 text-right">Δόση</th>
                <th className="px-4 py-3">Έναρξη</th><th className="px-4 py-3 text-center">Δόσεις</th>
                <th className="px-4 py-3">Συχνότητα</th>
                <th className="px-4 py-3">Επόμενη</th>{canEdit && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {list.map((o) => {
                const next = (o.schedule || []).find((s) => s.date >= new Date().toISOString().slice(0, 10));
                return (
                  <tr key={o.id} data-testid={`ob-row-${o.id}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1]">
                    <td className="px-4 py-2"><span className="px-2 py-0.5 rounded-full bg-[#F1EDE6] text-xs font-medium text-[#45423C]">{o.type_label}</span></td>
                    <td className="px-4 py-2 font-medium text-[#45423C]">{o.creditor || "—"}</td>
                    <td className="px-4 py-2 text-[#6F6A60]">{o.description || "—"}</td>
                    <td className="px-4 py-2 text-right tabular">{fmtEuro(o.amount_total)}</td>
                    <td className="px-4 py-2 text-right tabular font-semibold">{fmtEuro(o.instalment)}</td>
                    <td className="px-4 py-2 tabular text-xs">{fmtDate(o.start_date)}</td>
                    <td className="px-4 py-2 text-center tabular">{o.months}</td>
                    <td className="px-4 py-2 text-xs text-[#6F6A60]">{o.frequency_label || "Μηνιαία"}</td>
                    <td className="px-4 py-2 text-xs text-[#6F6A60]">{next ? `${fmtDate(next.date)} · ${fmtEuro(next.amount)}` : "—"}</td>
                    {canEdit && (
                      <td className="px-4 py-2 text-right">
                        <button data-testid={`ob-delete-${o.id}`} onClick={() => delOb.mutate(o.id)} className="p-1.5 rounded-lg text-[#6F6A60] hover:bg-[#F1EDE6]">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {list.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-8 text-center text-[#6F6A60]">{isCheques ? "Καμία επιταγή καταχωρημένη." : "Κανένα δάνειο καταχωρημένο."}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
